import axios from "axios";
import axioInstance from "../../../../../../../../../custom_utilities/AxiosInstance/axiosInstance";
import { url_299, url_store_package_view } from "../../../../../../../../../custom_utilities/api_services";
import {
  findPackageById,
  filterValidVariantOptions,
  normalizeStorePackageViewResponse,
} from "./storePackageUtils";
import {
  storePriceToastMessage,
  toastifyMessage,
} from "../../../../../../../../../custom_utilities/toastify_config";

const calculateFinalAmount = (priceDetails) => {
  const coursePrice = Number(priceDetails?.course_price ?? 0);
  const discount = Number(priceDetails?.discount ?? 0);
  return (coursePrice - discount).toFixed(2);
};

export const fetchStorePackageView = async (packageId) => {
  const response = await axioInstance.get(
    `${url_store_package_view}?package_id=${packageId}`
  );

  if (response.data?.status !== 200) {
    throw new Error(response.data?.message || "Failed to load package details.");
  }

  const normalized = normalizeStorePackageViewResponse(response.data?.data);
  if (!normalized) {
    throw new Error("No data received from API");
  }

  return normalized;
};

const selectPackage = (
  packageId,
  packageVariants,
  setSelectedPackage,
  packageData = null
) => {
  const parentId = packageData?.PackageInstallmentDetails?.package_id;
  const options = filterValidVariantOptions(
    packageVariants?.quick_variants_selection_options ?? [],
    parentId
  );

  const packageFromOptions = findPackageById(
    packageId,
    options,
    packageVariants
  );
  const installmentDetails = packageData?.PackageInstallmentDetails;
  const defaultPackage = packageVariants?.quick_variants_selection_default;

  setSelectedPackage(
    packageFromOptions ||
      (Number(installmentDetails?.package_id) === Number(packageId)
        ? installmentDetails
        : null) ||
      defaultPackage
  );
};

export const fetchPackageData = async ({
  packageId,
  isInitialLoad,
  setPackageData,
  setStoreCurrentPackageDetails,
  setFinalAmount,
  setInitialPackageVariants,
  setSelectedPackage,
  startLoading,
  finishLoading,
}) => {
  try {
    startLoading();

    const data = await fetchStorePackageView(packageId);

    setPackageData(data);
    setStoreCurrentPackageDetails(data);
    setFinalAmount(calculateFinalAmount(data.price_details));

    if (isInitialLoad) {
      const variants = data.package_quick_variants;
      setInitialPackageVariants({
        ...variants,
        quick_variants_selection_options: filterValidVariantOptions(
          variants?.quick_variants_selection_options ?? [],
          data?.PackageInstallmentDetails?.package_id
        ),
      });
    }

    selectPackage(
      packageId,
      data.package_quick_variants,
      setSelectedPackage,
      data
    );
  } catch (error) {
    console.error("Error fetching package data:", error.message || error);
    toastifyMessage({
      status: "error",
      message:
        error.message || "Failed to load package details. Please try again.",
    });
  } finally {
    finishLoading();
  }
};

export const applyCoupon = async ({
  couponCode,
  userId,
  packageId,
  selectedPackage,
  packageData,
  setFinalAmount,
  setCouponDiscount,
  setAddonDiscount,
  startLoading,
  finishLoading,
  handleCloseCoupon,
  setIsCouponApplied,
  finalAmount,
  paymentAction,
  setPaytmDataInStore,
}) => {
  if (!couponCode) {
    storePriceToastMessage({
      status: "error",
      message: "Please enter a coupon code.",
    });
    setCouponDiscount(0);
    setFinalAmount(calculateFinalAmount(packageData?.price_details) || "0.00");
    return;
  }

  if (!userId || !packageId) {
    console.error("Invalid userId or packageId:", { userId, packageId });
    storePriceToastMessage({
      status: "error",
      message: "Invalid user or package information.",
    });
    setCouponDiscount(0);
    setFinalAmount(calculateFinalAmount(packageData?.price_details) || "0.00");
    return;
  }

  try {
    startLoading();

    const initiateResponse = await axios.post(url_299, {
      user_id: userId,
      product_id: packageId,
      platform: "WEB",
    });

    if (initiateResponse.data.status !== 200) {
      throw new Error(
        initiateResponse.data?.message || "Failed to create order for coupon."
      );
    }

    const initiateData = initiateResponse.data?.data || initiateResponse.data;
    const resolvedOrderId =
      initiateData?.order_id || initiateData?.Order_Id || "";

    if (!resolvedOrderId) {
      throw new Error("Order id not received from initiate API.");
    }

    if (paymentAction) {
      paymentAction(resolvedOrderId);
    }
    if (setPaytmDataInStore) {
      setPaytmDataInStore(initiateData);
    }

    const payload = {
      is_coupon: true,
      user_id: userId,
      package_id: packageId,
      order_id: resolvedOrderId,
      amount: finalAmount,
      coupon_code: couponCode,
    };

    const response = await axios.post(
      "https://learning.motion.ac.in/motioneducation/api/user/apply_couponweb",
      payload
    );

    if (response.data.status === 200 && response.data.data.status === 200) {
      const updatedAmount = Number(response.data.data.updated_amount);
      const previousAmount = Number(
        finalAmount || calculateFinalAmount(packageData?.price_details)
      );
      const actualCouponDiscount = Math.max(0, previousAmount - updatedAmount);

      setFinalAmount(updatedAmount.toFixed(2));
      setCouponDiscount(actualCouponDiscount.toFixed(2));
      setAddonDiscount(0);
      setIsCouponApplied(true);

      storePriceToastMessage({
        status: "success",
        message: response.data.data.message,
      });
      handleCloseCoupon();
    } else {
      setCouponDiscount(0);
      setAddonDiscount(0);
      setFinalAmount(calculateFinalAmount(packageData?.price_details) || "0.00");
      setIsCouponApplied(false);
      storePriceToastMessage({
        status: "error",
        message: response.data.data.message || response.data.message,
      });
    }
  } catch (error) {
    console.error("Error applying coupon:", error);

    setCouponDiscount(0);
    setAddonDiscount(0);
    setFinalAmount(calculateFinalAmount(packageData?.price_details) || "0.00");
    setIsCouponApplied(false);

    storePriceToastMessage({
      status: "error",
      message: error.message || "Failed to apply coupon. Please try again.",
    });
  } finally {
    finishLoading();
  }
};
